import { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Info, GitCompare } from 'lucide-react';
import { apiClient } from '../../../api';
import type { ImportBatch } from './types';

interface SourceFreshness { latest_batch_id: number; latest_as_of: string | null; age_days: number | null; stale: boolean }

interface BaselineDiff {
  source: string;
  from: { batch_id: number; as_of: string | null; filename: string | null };
  to: { batch_id: number; as_of: string | null; filename: string | null };
  from_rows: number;
  to_rows: number;
  added: { count: number; sample: { eai_nbr: number; name: string | null }[] };
  removed: { count: number; sample: { eai_nbr: number; name: string | null }[] };
  changed: { count: number; sample: { eai_nbr: number; name: string | null; changes: { field: string; from: unknown; to: unknown }[] }[]; by_field: Record<string, number> };
  unchanged: number;
}

const DIFFABLE = new Set(['universe', 'phase2', 'phase2_oos']);

export default function ImportBaselines({ onImported }: { onImported: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLInputElement>(null);
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [freshness, setFreshness] = useState<Record<string, SourceFreshness>>({});
  const [busy, setBusy] = useState(false);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [kind, setKind] = useState<'auto' | 'phase2' | 'phase3' | 'universe'>('auto');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suiteTitle, setSuiteTitle] = useState('');
  const [relatedEai, setRelatedEai] = useState('');
  const [diff, setDiff] = useState<BaselineDiff | null>(null);
  const [diffing, setDiffing] = useState<number | null>(null);

  const reload = () => {
    apiClient.get('/api/cloud-portfolio/imports')
      .then((r) => { setBatches(r.data.items || []); setFreshness(r.data.freshness || {}); })
      .catch(() => { setBatches([]); setFreshness({}); });
  };

  const prevBatchOf = (b: ImportBatch) => batches.find((o) => o.source === b.source && o.id !== b.id && new Date(o.created_at) < new Date(b.created_at));

  const loadDiff = async (b: ImportBatch) => {
    const prev = prevBatchOf(b);
    if (!prev) return;
    setDiffing(b.id);
    setError(null);
    try {
      const r = await apiClient.get('/api/cloud-portfolio/baselines/diff', {
        params: { source: b.source, from: prev.id, to: b.id },
      });
      setDiff(r.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Diff failed');
    } finally {
      setDiffing(null);
    }
  };

  useEffect(() => { reload(); }, []);

  const uploadWorkbook = async (file: File) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('as_of', asOf);
      form.append('type', kind);
      const res = await apiClient.post('/api/cloud-portfolio/imports', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const counts = res.data.counts || {};
      const bits = Object.entries(counts).map(([k, v]) => `${k}: ${v}`);
      setMessage(`Imported ${file.name} (${res.data.as_of}). ${bits.join(' · ')}${res.data.warnings?.length ? `. Warnings: ${res.data.warnings.join('; ')}` : ''}`);
      reload();
      onImported();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const uploadCard = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (suiteTitle) form.append('suite_title', suiteTitle);
      if (relatedEai) form.append('related_eai', relatedEai);
      await apiClient.post('/api/cloud-portfolio/attachments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage(`Linked baseball-card file ${file.name} to EAI ${relatedEai || '(none)'}.`);
      reload();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Attachment failed');
    } finally {
      setBusy(false);
      if (cardRef.current) cardRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        Client workbooks stay local — they are parsed into tenant-scoped rows and are not committed to git.
        Phase 3 Cat 1–3 rows are Excel-hidden; this importer includes them. SoR columns are never overwritten by analysis fields.
      </div>

      <div className="border border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Workbook import</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">
            as of
            <input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              className="block mt-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
            />
          </label>
          <label className="text-xs text-gray-600">
            type
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              className="block mt-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
            >
              <option value="auto">Auto-detect sheets</option>
              <option value="phase2">Phase 2 (Application Details)</option>
              <option value="phase3">Phase 3 (Application Tracker)</option>
              <option value="universe">Universe (ServiceNow EAI extract)</option>
            </select>
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload .xlsx
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadWorkbook(f); }}
          />
        </div>
        <p className="text-[11px] text-gray-400">
          Expected: Final Application data.xlsx (Application Details, Field Definition, Out of Scope, Service Now Remediations),
          Cloud_Migration_Application_Tracker.xlsx (Application Tracker + Operating Model), and the ServiceNow EAI extract
          (&quot;New&quot; tab with install_status / u_agc_status / u_primary_data_center) for datacentre scopes.
        </p>
      </div>

      <div className="border border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Baseball card (suite attachment)</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">
            Suite title
            <input
              value={suiteTitle}
              onChange={(e) => setSuiteTitle(e.target.value)}
              placeholder="e.g. Oracle E-Business Suite"
              className="block mt-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-56"
            />
          </label>
          <label className="text-xs text-gray-600">
            Related EAI (comma-separated)
            <input
              value={relatedEai}
              onChange={(e) => setRelatedEai(e.target.value)}
              placeholder="10006, 10019"
              className="block mt-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-48"
            />
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => cardRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Attach PPTX
          </button>
          <input
            ref={cardRef}
            type="file"
            accept=".pptx,.ppt"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCard(f); }}
          />
        </div>
      </div>

      {message && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">{message}</div>
      )}
      {error && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">{error}</div>
      )}

      {Object.keys(freshness).length > 0 && (
        <div className="border border-gray-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Freshness — age is measured from the as-of date, not the upload time</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(freshness).map(([source, f]) => (
              <span
                key={source}
                className={`text-xs rounded-full px-2.5 py-1 border ${
                  f.stale ? 'text-amber-800 bg-amber-50 border-amber-200 font-semibold' : 'text-gray-600 bg-gray-50 border-gray-200'
                }`}
              >
                {source}: {f.latest_as_of || '—'}{f.age_days != null ? ` · ${f.age_days === 0 ? 'today' : `${f.age_days}d old`}` : ''}{f.stale ? ' — stale' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Baselines — dated and immutable, never a silent mutation of the current sheet</p>
        {batches.length === 0 ? (
          <p className="text-xs text-gray-400">No imports yet.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-500">
                  <th className="text-left font-semibold px-3 py-2">When</th>
                  <th className="text-left font-semibold px-3 py-2">as of</th>
                  <th className="text-left font-semibold px-3 py-2">Source</th>
                  <th className="text-left font-semibold px-3 py-2">File</th>
                  <th className="text-left font-semibold px-3 py-2">Rows</th>
                  <th className="text-left font-semibold px-3 py-2">Diff</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="even:bg-gray-50 border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-500">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{b.as_of || '—'}</td>
                    <td className="px-3 py-2 font-medium">{b.source}</td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{b.filename || '—'}</td>
                    <td className="px-3 py-2">{Object.entries(b.row_counts || {}).map(([k, v]) => `${k} ${v}`).join(', ') || '—'}</td>
                    <td className="px-3 py-2">
                      {DIFFABLE.has(b.source) && prevBatchOf(b) ? (
                        <button
                          onClick={() => loadDiff(b)}
                          disabled={diffing === b.id}
                          className="flex items-center gap-1 text-amber-700 font-semibold hover:underline disabled:opacity-50"
                        >
                          {diffing === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitCompare className="w-3 h-3" />}
                          vs previous
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {diff && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-super-dark-purple">
            Diff · {diff.source} — {diff.from.as_of || `#${diff.from.batch_id}`} → {diff.to.as_of || `#${diff.to.batch_id}`}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Added</p>
              <p className="text-xl font-bold text-super-dark-purple">{diff.added.count}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Removed</p>
              <p className="text-xl font-bold text-super-dark-purple">{diff.removed.count}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Changed</p>
              <p className="text-xl font-bold text-super-dark-purple">{diff.changed.count}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Unchanged</p>
              <p className="text-xl font-bold text-super-dark-purple">{diff.unchanged}</p>
            </div>
          </div>
          {Object.keys(diff.changed.by_field).length > 0 && (
            <p className="text-[11px] text-gray-500">
              Changes by field: {Object.entries(diff.changed.by_field).sort((a, b) => b[1] - a[1]).map(([f, n]) => `${f} (${n})`).join(' · ')}
            </p>
          )}
          {diff.added.count > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer font-semibold">Added — first {diff.added.sample.length}</summary>
              {diff.added.sample.map((a) => <p key={a.eai_nbr} className="ml-3 text-[11px]">EAI {a.eai_nbr} · {a.name || '—'}</p>)}
            </details>
          )}
          {diff.removed.count > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer font-semibold">Removed — first {diff.removed.sample.length}</summary>
              {diff.removed.sample.map((a) => <p key={a.eai_nbr} className="ml-3 text-[11px]">EAI {a.eai_nbr} · {a.name || '—'}</p>)}
            </details>
          )}
          {diff.changed.count > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer font-semibold">Changed — first {diff.changed.sample.length}</summary>
              {diff.changed.sample.map((a) => (
                <p key={a.eai_nbr} className="ml-3 text-[11px]">
                  EAI {a.eai_nbr} · {a.name || '—'} — {a.changes.map((c) => `${c.field}: '${c.from ?? ''}' → '${c.to ?? ''}'`).join('; ')}
                </p>
              ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
}
